import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";
import { CoverBlock } from "./blocks/CoverBlock";
import { TextBlock } from "./blocks/TextBlock";
import { ImageBlock } from "./blocks/ImageBlock";
import { LineItemsTable } from "./blocks/LineItemsTable";
import { SignatureBlock } from "./blocks/SignatureBlock";
import { GoodBetterBestBlock } from "./blocks/GoodBetterBestBlock";
import { TermsBlock } from "./blocks/TermsBlock";
import { MarketingGalleryBlock } from "./blocks/MarketingGalleryBlock";
import { TotalsBlock } from "./blocks/TotalsBlock";
import type { TemplateLayout, Block } from "@/types/proposals";

// Standard page dimensions (8.5" × 11" at 120 DPI)
const PAGE_WIDTH = 1020;
const PAGE_HEIGHT = 1320;

interface ProposalPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layout: TemplateLayout;
  title: string;
}

export function ProposalPreviewDialog({
  open,
  onOpenChange,
  layout,
  title,
}: ProposalPreviewDialogProps) {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [zoom, setZoom] = useState(0.7); // Default zoom to fit in viewport

  const pages = layout.pages;
  const currentPage = pages[currentPageIndex];

  const renderBlock = (block: Block) => {
    const { x = 0, y = 0, width = 400, height = 200 } = block.position || {};
    
    const blockStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${x}px`,
      top: `${y}px`,
      width: `${width}px`,
      height: height === 'auto' ? 'auto' : `${height}px`,
    };

    const props = {
      block,
      onUpdate: () => {},
      onSelect: () => {},
      isPreview: true,
    };

    return (
      <div key={block.id} style={blockStyle} className="pointer-events-none">
        {(() => {
          switch (block.type) {
            case "cover":
              return <CoverBlock {...props} />;
            case "text":
              return <TextBlock {...props} />;
            case "image":
              return <ImageBlock {...props} />;
            case "line_items_table":
              return <LineItemsTable {...props} />;
            case "signature":
              return <SignatureBlock {...props} />;
            case "good_better_best":
              return <GoodBetterBestBlock {...props} />;
            case "terms":
              return <TermsBlock {...props} />;
            case "marketing_gallery":
              return <MarketingGalleryBlock {...props} />;
            case "totals_summary":
              return <TotalsBlock {...props} />;
            default:
              return null;
          }
        })()}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 gap-0 flex flex-col">
        {/* Header */}
        <div className="border-b bg-card px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-xl font-bold">{title}</h2>
              <p className="text-sm text-muted-foreground">
                Preview Mode - Page {currentPageIndex + 1} of {pages.length}
              </p>
            </div>
            <div className="flex items-center gap-2 border-l pl-4">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setZoom(Math.max(0.3, zoom - 0.1))}
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium min-w-[60px] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setZoom(Math.min(1.5, zoom + 0.1))}
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-8 bg-muted/30 min-h-0 flex items-center justify-center">
          <div 
            className="bg-white shadow-2xl"
            style={{
              width: `${PAGE_WIDTH * zoom}px`,
              height: `${PAGE_HEIGHT * zoom}px`,
              transform: `scale(1)`,
              transformOrigin: 'center center',
            }}
          >
            <div 
              className="relative w-full h-full"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'top left',
                width: `${PAGE_WIDTH}px`,
                height: `${PAGE_HEIGHT}px`,
              }}
            >
              {currentPage?.blocks
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((block) => renderBlock(block))}
            </div>
          </div>
        </div>

        {/* Footer - Navigation */}
        {pages.length > 1 && (
          <div className="border-t bg-card px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between max-w-4xl mx-auto">
              <Button
                variant="outline"
                onClick={() => setCurrentPageIndex(Math.max(0, currentPageIndex - 1))}
                disabled={currentPageIndex === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>

              <div className="text-sm text-muted-foreground">
                Page {currentPageIndex + 1} of {pages.length}
              </div>

              <Button
                variant="outline"
                onClick={() =>
                  setCurrentPageIndex(Math.min(pages.length - 1, currentPageIndex + 1))
                }
                disabled={currentPageIndex === pages.length - 1}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
